import React, { useState } from "react";
import { Box, Theme, Typography, useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";
import { format, parseJSON } from "date-fns";
import {
  Booking,
  Investment,
  InvestmentTypeEnum,
  useListAndSearchAllBookings,
} from "@bn/api/apiv1";
import {
  getNumberOfLotsByInvestmentAndBookingType,
  getValueByInvestmentAndBookingType,
  toCurrency,
} from "@helpers";
import {
  BookingType,
  LoadingPlaceHolder,
  Note,
  ScrollableWithGradients,
} from "@components";
import { BOOKINGS_COMPATIBILITY_VERSION } from "@constants";
import { TransactionStatusIcon } from "@features/investments/investment/components/TransactionStatusIcon";
import { BookingActionMenu } from "./BookingActionMenu";
import { DeleteBookingDialog } from "./DeleteBookingDialog";
import { EditBookingModal, isBookingEditable } from "./EditBookingModal";

interface BookingRowProps {
  booking: Booking;
  onEdit: () => void;
  onDelete: () => void;
}

const BookingRow = ({ booking, onEdit, onDelete }: BookingRowProps) => {
  const numberOfLotsText = getNumberOfLotsByInvestmentAndBookingType({
    value: booking.numberOfLots,
    investmentType: booking.investment?.type as InvestmentTypeEnum,
    bookingType: booking.type,
  });
  const priceText = getValueByInvestmentAndBookingType({
    value: Math.abs(booking.securityPrice),
    investmentType: booking.investment?.type as InvestmentTypeEnum,
    bookingType: booking.type,
    currency: booking.investment?.currency,
    percentageRange: "0-100",
  });
  const subtitle = numberOfLotsText
    ? `${numberOfLotsText} ${priceText}`
    : priceText;
  const showEdit = isBookingEditable(booking.type);

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      py={2}
      px={5}
      sx={{
        borderBottom: (theme) => `1px solid ${theme.palette.border.secondary}`,
        "&:last-child": { borderBottom: "none" },
      }}
    >
      <Box>
        <BookingType type={booking.type} />
        <Box display="flex" gap={1} alignItems="center">
          <TransactionStatusIcon
            dataConsistencyStatus={booking.dataConsistencyStatus}
            dataSource={booking.dataSource}
            isExternalID={Boolean(booking.externalId)}
          />
          <Typography variant="body3" color="textSecondary" mt={0.5}>
            {format(parseJSON(booking.valueDate), "dd.MM.yyyy")}
          </Typography>
        </Box>
        <Note comment={booking.comment} />
      </Box>
      <Box display="flex" alignItems="center" gap={2}>
        <Box textAlign="right">
          <Typography
            variant="body2"
            fontWeight={600}
            color={booking.amount >= 0 ? "text.positive" : "text.negative"}
          >
            {toCurrency(booking.amount, booking.investment?.currency)}
          </Typography>
          <Typography variant="caption2" color="textSecondary" display="block">
            {subtitle}
          </Typography>
        </Box>
        <BookingActionMenu
          onEdit={showEdit ? onEdit : undefined}
          onDelete={onDelete}
          showEdit={showEdit}
        />
      </Box>
    </Box>
  );
};

export const BookingsList = ({
  investment,
  image,
}: {
  investment: Investment;
  image?: string | null;
}) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );
  const isLessMd = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );
  const { data, isFetching } = useListAndSearchAllBookings({
    investmentId: [investment.id],
    enumCompatibilityVersion: BOOKINGS_COMPATIBILITY_VERSION,
    order: ["valueDate desc"],
  });

  const [editingBooking, setEditingBooking] = useState<Booking>();
  const [deletingBooking, setDeletingBooking] = useState<Booking>();

  if (isFetching)
    return (
      <Box mt={4} px={6}>
        <LoadingPlaceHolder
          height={isMobile ? "320px" : isLessMd ? "320px" : "296px"}
        />
      </Box>
    );

  const bookings = data?.bookings ?? [];

  if (bookings.length === 0) {
    return (
      <Box py={4} textAlign="center">
        <Typography variant="body3" color="textSecondary">
          {t("domains.investments.investmentTable.detailModal.noBookings")}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <ScrollableWithGradients
        maxHeight={{ xs: 336, md: 312 }}
        minHeight={isMobile ? 336 : 312}
      >
        {bookings.map((booking) => (
          <BookingRow
            key={booking.id}
            booking={booking}
            onEdit={() => setEditingBooking(booking)}
            onDelete={() => setDeletingBooking(booking)}
          />
        ))}
      </ScrollableWithGradients>

      <EditBookingModal
        booking={editingBooking}
        investmentId={investment.id}
        image={image}
        isOpen={!!editingBooking}
        onClose={() => setEditingBooking(undefined)}
        onSuccess={() => setEditingBooking(undefined)}
      />

      {deletingBooking && (
        <DeleteBookingDialog
          bookingId={deletingBooking.id}
          isOpen
          onClose={() => setDeletingBooking(undefined)}
        />
      )}
    </>
  );
};
